/* eslint-disable react-hooks/exhaustive-deps */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';

import Close from '@/assets/svgs/close.svg?react';
import Logo from '@/assets/svgs/logo.svg?react';
import Menu from '@/assets/svgs/menu.svg?react';
import User from '@/assets/svgs/user.svg?react';
import { NavigationMenuItem } from '@/components/layout/navigation-menu-item';
import { Text } from '@/components/Text';
import type { ResourcePermission } from '@/lib/data';
import { useAuthStore } from '@/store/auth';

export type MenuItem = {
  name: string;
  label: string;
  path: string;
  items?: MenuItem[];
};

export const NavigationMenu = () => {
  // Component stores
  const { user } = useAuthStore();

  // Component states
  const [isExpanded, setIsExpanded] = useState(false);

  // Convert API permissions → menu tree.
  // Group nodes (Leads, Warranty, Marketing, Sales Hub, Admin Panel) carry a
  // `name` and `children` but NO `resourcePath` — they render as collapsible
  // sections, never as links, so they get an empty path.
  // `isHidden` entries are granted but unlisted: the route stays reachable by
  // URL (PermissionGuard reads the same tree), it just gets no sidebar row.
  const buildMenu = (items?: ResourcePermission[]): MenuItem[] => {
    if (!items) return [];

    return items
      .filter((item) => !item.isHidden)
      .map((item) => ({
        name: item.name ?? item.resourcePath ?? '',
        label: item.label ?? '',
        path: item.resourcePath ? `/${item.resourcePath}` : '',
        items: item.children?.length ? buildMenu(item.children) : undefined,
      }));
  };

  // Component defaults
  const dashboardItem = useMemo<MenuItem | null>(() => {
    const dashboard = user?.permissions?.dashboard;

    if (!dashboard?.length) return null;

    return {
      name: 'dashboard',
      label: 'Dashboard',
      path: '/',
    };
  }, [user]);
  const menu = useMemo(() => {
    return buildMenu(user?.permissions?.web);
  }, [user]);

  useEffect(() => {
    document.body.style.overflow = isExpanded ? 'hidden' : '';
  }, [isExpanded]);

  const renderMenu = () => {
    return (
      <>
        {dashboardItem && (
          <NavigationMenuItem
            item={dashboardItem}
            closeMenu={() => setIsExpanded(false)}
          />
        )}

        {menu.map((x, index) => (
          <NavigationMenuItem
            key={index}
            item={x}
            closeMenu={() => setIsExpanded(false)}
          />
        ))}

        <div className='w-full border-t border-white_20 mt-3 pt-3'>
          <NavigationMenuItem
            item={{ name: 'settings', label: 'Settings', path: '/settings' }}
            closeMenu={() => setIsExpanded(false)}
          />
          <NavigationMenuItem
            item={{ name: 'sign-out', label: 'Sign Out', path: '/sign-out' }}
            closeMenu={() => setIsExpanded(false)}
          />
        </div>
      </>
    );
  };

  const renderUserDetails = (
    <div className='flex items-center gap-3 mt-5.5 sm:mt-8 pb-4 sm:pb-6'>
      <div className='shrink-0 w-10 h-10 bg-white rounded-full flex justify-center items-center'>
        <User className='size-6! text-greyDark_60' />
      </div>

      <div className='flex flex-col justify-between min-w-0'>
        <Text renderAs='psmallbold' className='text-white'>
          {user?.firstName} {user?.lastName}
        </Text>
        <Text renderAs='psmall' className='text-white wrap-break-word'>
          {user?.email}
        </Text>
      </div>
    </div>
  );

  return (
    <div
      id='navigation-menu'
      className='sticky top-0 z-50 shrink-0 bg-greyDark w-screen sm:w-64 h-full sm:h-screen overflow-y-auto overflow-x-hidden px-4 pt-4 sm:pt-6 flex flex-col justify-between'
    >
      <div className='pb-4 sm:pb-0 flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-normal'>
        <Logo className='w-53 h-10' />

        {/* R7: was a bare <div> with an onClick — not focusable, no role, no label.
            Below `sm` this is the ONLY route to the navigation, so keyboard and
            screen-reader users had no way to reach it at all. Semantics only; the
            visual treatment is unchanged. */}
        <button
          type='button'
          aria-label={isExpanded ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isExpanded}
          aria-controls='mobile-navigation-drawer'
          className='sm:hidden w-9 h-9 bg-white rounded-full flex justify-center items-center cursor-pointer'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <Close className='w-[21.6px] h-[21.6px] text-greenDark' />
          ) : (
            <Menu className='w-[21.6px] h-[21.6px] text-greenDark' />
          )}
        </button>

        <div className='hidden sm:block w-full mt-8'>{renderMenu()}</div>
      </div>

      <div className='hidden sm:block w-full'>{renderUserDetails}</div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial='collapsed'
            animate='open'
            exit='collapsed'
            variants={{
              open: { height: 'calc(100vh - 94px)' },
              collapsed: { height: 0 },
            }}
            transition={{
              duration: 0.3,
              ease: [0.04, 0.62, 0.23, 0.98],
            }}
            id='mobile-navigation-drawer'
            className='sm:hidden mt-5.5 flex flex-col justify-between'
          >
            <div>{renderMenu()}</div>

            {renderUserDetails}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
